import React, { useState, useEffect } from 'react';
import { Grid, TextField, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

function UserForm({ formData, roles, isEditMode, onInputChange, onValidationChange }) {
  const { t } = useTranslation();
  const [phoneError, setPhoneError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [positionError, setPositionError] = useState('');

  // Función para validar email
  const validateEmail = (email) => {
    if (!email) return t('Email required');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return t('Invalid email');
    return '';
  };

  // Función para validar campos requeridos
  const validateRequired = (value, fieldName) => {
    if (!value || !value?.trim()) return t('Field required', { field: fieldName });
    return '';
  };

  // Función para validar contraseña
  const validatePassword = (password) => {
    if (!password) return t('Password required');

    if (password.length < 8) {
      return t('Password min length');
    }

    // Verificar que tenga al menos una mayúscula
    if (!/[A-Z]/.test(password)) {
      return t('Password uppercase');
    }

    // Verificar que tenga al menos una letra (mayúscula o minúscula)
    if (!/[a-zA-Z]/.test(password)) {
      return t('Password letter');
    }

    // Verificar que tenga al menos un número
    if (!/[0-9]/.test(password)) {
      return t('Password number');
    }

    // Verificar que tenga al menos un carácter especial que NO sea punto
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,<>\/?~`]/.test(password)) {
      return t('Password special char');
    }

    // Verificar que NO contenga punto
    if (/\./.test(password)) {
      return t('Password no dot');
    }

    return '';
  };

  // Función para validar todo el formulario
  const validateAllFields = () => {
    const errors = {};
    if (!isEditMode) {
      errors.firstName = validateRequired(formData.firstName, t('Name'));
      errors.lastName = validateRequired(formData.lastName, t('Last name'));
      errors.password = validatePassword(formData.password || '');
    } else {
      errors.fullName = validateRequired(formData.fullName, t('Full name'));
    }

    // Validaciones comunes
    if (!isEditMode) {
      errors.email = validateEmail(formData.email || '');
    }
    errors.phone = validatePhone(formData.phone || '');
    errors.role = validateRequired(formData.role, t('Role'));
    errors.position = validateRequired(formData.position, t('Position'));

    // Filtrar errores vacíos
    const filteredErrors = Object.fromEntries(
      Object.entries(errors).filter(([key, value]) => value !== ''),
    );

    return filteredErrors;
  };

  useEffect(() => {
    if (onValidationChange) {
      const errors = validateAllFields();
      const hasErrors = Object.keys(errors).length > 0;
      onValidationChange(hasErrors, errors);
    }
  }, [formData, isEditMode]);

  // Función para validar teléfono internacional
  const validatePhone = (phone) => {
    if (!phone) return t('Phone required');

    // Limpiar el teléfono (remover espacios, guiones, paréntesis)
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

    // Validación básica: debe empezar con + seguido de números
    if (cleanPhone.startsWith('+')) {
      // Formato internacional: +[código país][número]
      const internationalRegex = /^\+[1-9]\d{7,14}$/;
      if (!internationalRegex.test(cleanPhone)) {
        return t('Invalid international phone');
      }

      // Validar longitud total (código país + número)
      if (cleanPhone.length < 10 || cleanPhone.length > 16) {
        return t('Phone length error');
      }
    } else {
      // Número local (sin código de país) - asumimos Chile por defecto
      const localRegex = /^[0-9]{8,9}$/;
      if (!localRegex.test(cleanPhone)) {
        return t('Local phone error');
      }
    }

    return '';
  };

  // Formatear teléfono automáticamente
  const formatPhone = (value) => {
    // Remover caracteres que no sean números, + o espacios
    const cleaned = value.replace(/[^\d+\s]/g, '');

    // Si ya empieza con +, mantenerlo
    if (cleaned.startsWith('+')) {
      return cleaned;
    }

    // Si empieza con 56 y no tiene +, agregar + (para Chile)
    if (cleaned.startsWith('56') && cleaned.length > 2) {
      return `+${cleaned}`;
    }

    // Si es un número chileno típico (9 dígitos empezando con 9), agregar +56
    if (cleaned.length === 9 && cleaned.startsWith('9')) {
      return `+56${cleaned}`;
    }

    // Para otros casos, devolver tal como está
    return cleaned;
  };

  // Manejar cambio de teléfono
  const handlePhoneChange = (value) => {
    const formattedPhone = formatPhone(value);
    onInputChange('phone', formattedPhone);

    // Validar en tiempo real
    const error = validatePhone(formattedPhone);
    setPhoneError(error);
  };

  // Validar cuando el usuario sale del campo
  const handlePhoneBlur = () => {
    const error = validatePhone(formData.phone || '');
    setPhoneError(error);
  };

  // Manejar cambio de contraseña
  const handlePasswordChange = (value) => {
    onInputChange('password', value);

    // Validar en tiempo real
    const error = validatePassword(value);
    setPasswordError(error);
  };

  // Validar contraseña cuando el usuario sale del campo
  const handlePasswordBlur = () => {
    const error = validatePassword(formData.password || '');
    setPasswordError(error);
  };

  // Manejar otros campos con validación
  const handleEmailChange = (value) => {
    const normalizedEmail = value.toLowerCase().trim();
    onInputChange('email', normalizedEmail);
    if (!isEditMode) {
      const error = validateEmail(normalizedEmail);
      setEmailError(error);
    }
  };

  const handleEmailBlur = () => {
    if (!isEditMode) {
      const error = validateEmail(formData.email || '');
      setEmailError(error);
    }
  };

  const handleNameChange = (value) => {
    onInputChange('firstName', value);
    const error = validateRequired(value, t('Name'));
    setNameError(error);
  };

  const handleLastNameChange = (value) => {
    onInputChange('lastName', value);
    const error = validateRequired(value, t('Last name'));
    setLastNameError(error);
  };

  const handleFullNameChange = (value) => {
    onInputChange('fullName', value);
    const error = validateRequired(value, t('Full name'));
    setFullNameError(error);
  };

  const handleRoleChange = (value) => {
    onInputChange('role', value);
    const error = validateRequired(value, t('Role'));
    setRoleError(error);
  };

  const handlePositionChange = (value) => {
    onInputChange('position', value);
    const error = validateRequired(value, t('Position'));
    setPositionError(error);
  };

  return (
    <Grid container spacing={3}>
      {/* MODO CREAR - Campos separados */}
      {!isEditMode ? (
        <>
          {/* Nombre */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('Name')}
              value={formData.firstName || ''}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => setNameError(validateRequired(formData.firstName, t('Name')))}
              fullWidth
              required
              variant="outlined"
              error={!!nameError}
              helperText={nameError}
            />
          </Grid>

          {/* Apellido */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('Last name')}
              value={formData.lastName || ''}
              onChange={(e) => handleLastNameChange(e.target.value)}
              onBlur={() => setLastNameError(validateRequired(formData.lastName, t('Last name')))}
              fullWidth
              required
              variant="outlined"
              error={!!lastNameError}
              helperText={lastNameError}
            />
          </Grid>
        </>
      ) : (
        <>
          {/* MODO EDITAR - Solo nombre completo */}
          <Grid item xs={12}>
            <TextField
              label={t('Full name')}
              value={formData.fullName || ''}
              onChange={(e) => handleFullNameChange(e.target.value)}
              onBlur={() =>
                setFullNameError(validateRequired(formData.fullName, t('Full name')))
              }
              fullWidth
              required
              variant="outlined"
              placeholder={t('Example Name')}
              error={!!fullNameError}
              helperText={fullNameError}
            />
          </Grid>
        </>
      )}

      {/* Email - Común para ambos modos */}
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('Email')}
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={handleEmailBlur}
          fullWidth
          required
          variant="outlined"
          disabled={isEditMode}
          InputProps={{
            readOnly: isEditMode,
          }}
          error={!!emailError && !isEditMode}
          helperText={isEditMode ? t('Email readonly') : emailError}
        />
      </Grid>

      {/* Teléfono - Común para ambos modos - CON VALIDACIÓN */}
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('Phone')}
          value={formData.phone || ''}
          onChange={(e) => handlePhoneChange(e.target.value)}
          onBlur={handlePhoneBlur}
          fullWidth
          required
          variant="outlined"
          placeholder="+584164708089, +56912345678, 912345678"
          error={!!phoneError}
          helperText={phoneError || t('Phone helper')}
        />
      </Grid>

      {/* Rol - Común para ambos modos */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required variant="outlined">
          <InputLabel>{t('Role')}</InputLabel>
          <Select
            value={formData.role || ''}
            onChange={(e) => handleRoleChange(e.target.value)}
            onBlur={() => setRoleError(validateRequired(formData.role, t('Role')))}
            label={t('Role')}
            error={!!roleError}>
            {roles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Cargo - Común para ambos modos */}
      <Grid item xs={12} sm={6}>
        <TextField
          label={t('Position')}
          value={formData.position || ''}
          onChange={(e) => handlePositionChange(e.target.value)}
          onBlur={() => setPositionError(validateRequired(formData.position, t('Position')))}
          fullWidth
          required
          variant="outlined"
          placeholder={t('Example Position')}
          error={!!positionError}
          helperText={positionError}
        />
      </Grid>

      {/* Contraseña - Solo en modo crear */}
      {!isEditMode && (
        <Grid item xs={12}>
          <TextField
            label={t('Password')}
            type="password"
            value={formData.password || ''}
            onChange={(e) => handlePasswordChange(e.target.value)}
            onBlur={handlePasswordBlur}
            fullWidth
            required
            variant="outlined"
            placeholder={t('Password placeholder')}
            error={!!passwordError}
            helperText={passwordError || t('Password helper')}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default UserForm;
